import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { getBrand } from '../_getBrand';
import { algoliaConfig } from '../_config';
import { getProductTitleParts } from 'swatches';
import '../../scripts/product-card';

export function mountSRP() {
  const template = document.getElementById('product-card-template');
  const mountPoint = document.getElementById('algoliaSRP');
  if (!mountPoint) return; // not on the SRP, bail early

  const brand = getBrand();
  const config = algoliaConfig[brand];
  const searchClient = algoliasearch(
    config.algolia.appId,
    config.algolia.apiKey
  );

  // ==== HELPER FUNCTIONS ===================================
  // return the whole URL as a single object
  function getStateFromURL() {
    const params = new URLSearchParams(window.location.search);

    return {
      query: params.get('q') ?? '',
      page: Math.max(0, parseInt(params.get('page') ?? '1', 10) - 1), // Adjust 1-indexing to 0-indexing as Algolia requires
      filters: getFiltersFromURL(params),
    }
  }

  // return filters in string format
  function getFiltersFromURL(params) {
    // TODO: parse active facet refinements into an Algolia filters string.
    //
    // Intended URL pattern (multi-value within an attribute uses OR;
    // cross-attribute uses AND):
    //   ?q=dress&f_product_type=Tops&f_product_type=Blouses&f_vendor=Nike
    //   → '(product_type:"Tops" OR product_type:"Blouses") AND vendor:"Nike"'
    //
    // Read multi-value params with: params.getAll('f_ATTRIBUTENAME') → string[]
    // Implement once facets are finalized.
    return '';
  }

  function pushStateToURL({ query, page, filters }, replace = false) {
    const params = new URLSearchParams(window.location.search);

    if (query) {
      params.set('q', query);
    } else {
      params.delete('q');
    }

    if (page > 0) {
      // Store page as 1-indexed in the URL for readability
      params.set(('page'), String(page + 1));
    } else {
      params.delete('page');
    }

    // TODO: serialize active filters back to URL params here

    // replace the URL with the new one now that we have modified it
    const newURL = `${window.location.pathname}?${params.toString()}`;
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({ query, page, filters }, '', newURL);
  }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isBundleHit(hit) {
    const type = String(hit.product_type || hit.type || '').trim().toLowerCase();
    const tags = hit.tags ?? '';
    const tagList = Array.isArray(tags)
      ? tags.map((tag) => String(tag).trim().toLowerCase())
      : String(tags).split(',').map((tag) => tag.trim().toLowerCase());

    return (
      type === 'bundle' ||
      tagList.includes('bundle') ||
      hit.bundle === true
    );
  }

  /**
   * Returns an array of 0-indexed page numbers with '…' gap markers,
   * showing first page, last page, current page, and 1 page either side.
   */
  function getPaginationRange(current, total) {
    const delta = 1;
    const range = new Set([0, total - 1]);
    for (let i = Math.max(0, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.add(i);
    }
    const sorted = [...range].sort((a, b) => a - b);
    const result = [];
    let prev = -1;
    for (const p of sorted) {
      if (p - prev > 1) result.push('…');
      result.push(p);
      prev = p;
    }
    return result;
  }




  // ==== SEARCH =============================================
  async function runSearch({ query, page, filters }) {
    setLoadingState(true);

    try {
      const { results } = await searchClient.search({
        requests: [
          {
            indexName: 'shopify_products',
            query,
            hitsPerPage: 24,
            page: page,
            facets: ['*'], // intentional — returns all facets for inspection; narrow later
            filters: filters || undefined,
          },
          {
            indexName: 'shopify_collections',
            query,
            hitsPerPage: 24,
            page: 0,
          },
          {
            indexName: 'shopify_pages',
            query,
            hitsPerPage: 24,
            page: 0,
          },
        ],
      });

      const [productResults, collectionResults, pageResults] = results;

      renderStatus(productResults, query);
      await renderProductHits(productResults);
      renderCollectionHits(collectionResults);
      renderPageHits(pageResults);
      renderPagination(productResults, page);
      renderFacetPlaceholder(productResults);

    } catch (err) {
      console.error('[SRP] Search failed:', err);
      // TODO: render a user-facing error state
    } finally {
      setLoadingState(false);
    }
    // use these to plan facets
    // console.log('Products:', productResults);
    // console.log('Collections:', collectionResults);
    // console.log('Pages:', pageResults);
  }

  // ==== RENDER =============================================
  function setLoadingState(isLoading) {
    mountPoint.setAttribute(('data-loading'), String(isLoading));
  }

  function renderStatus(results, query) {
    const el = document.getElementById('srp-status');
    if (!el) return;
    if (!query) { el.innerHTML = ''; return; }
    if (results.nbHits > 0) {
      el.innerHTML = `Showing <strong>${results.nbHits.toLocaleString()}</strong> results for "<em>${escapeHTML(query)}</em>"`;
    } else {
      el.innerHTML = `No results for "<em>${escapeHTML(query)}</em>"`;
    }
  }

  function renderProductHits(results) {
    const el = document.getElementById('srp-product-hits');
    if (!el) return;

    el.innerHTML = '';
    for (const hit of results.hits) {
      console.log(hit);

      const [title, color] = getProductTitleParts(hit.title);
      const productUrl = `/products/${hit.handle}`;
      const swatchName = `Color-${hit.objectID}`;
      const swatchIdPrefix = `product-card-swatch-${hit.objectID}`;

      const wrap = document.createElement('div');
      wrap.className = 'search-result';
      wrap.dataset.objectid = hit.objectID;

      const isBundle = isBundleHit(hit);
      const sample = template?.content?.querySelector(isBundle ? 'product-card' : 'variant-product-card');
      const card = sample ? sample.cloneNode(true) : document.createElement(isBundle ? 'product-card' : 'variant-product-card');

      card.setAttribute('handle', hit.handle);
      if (!isBundle) card.setAttribute('product-title', title);
      if (!isBundle) card.setAttribute('product-url', productUrl);
      if (color) card.setAttribute('current-color', color);
      if (hit.queryBySale) card.setAttribute('query-by-sale', hit.queryBySale);

      const titleLink = card.querySelector('.product-title');
      if (titleLink) {
        titleLink.textContent = title;
        titleLink.href = productUrl;
      }

      const mediaLink = card.querySelector('.product-card__media');
      if (mediaLink) {
        mediaLink.href = productUrl;
      }

      const swatchFieldset = card.querySelector('[data-swatches]');
      if (swatchFieldset) {
        swatchFieldset.dataset.name = swatchName;
        swatchFieldset.dataset.swatchIdPrefix = swatchIdPrefix;
      }

      const placeholderInput = card.querySelector('input[type="radio"]');
      const placeholderLabel = card.querySelector('label[for="product-card-swatch-placeholder"]');
      if (placeholderInput && placeholderLabel) {
        const placeholderId = `${swatchIdPrefix}-placeholder`;
        placeholderInput.name = swatchName;
        placeholderInput.id = placeholderId;
        placeholderLabel.htmlFor = placeholderId;
      }

      wrap.appendChild(card);
      el.appendChild(wrap);
    }
  }


  function renderCollectionHits(results) {
    const el = document.getElementById('srp-collection-hits');
    if (!el) return;

    if (!results.hits.length) { el.hidden = true; return; }

    el.hidden = false;
    el.innerHTML = `
      <h2>Collections</h2>
      <ul>
        ${results.hits.map(hit => `
          <li><a href="/collections/${hit.handle}">${escapeHTML(hit.title ?? '')}</a></li>
        `).join('')}
      </ul>
    `;
  }

  function renderPageHits(results) {
    const el = document.getElementById('srp-page-hits');
    if (!el) return;

    if (!results.hits.length) { el.hidden = true; return; }

    el.hidden = false;
    el.innerHTML = `
      <h2>Pages</h2>
      <ul>
        ${results.hits.map(hit => `
          <li><a href="/pages/${hit.handle}">${escapeHTML(hit.title ?? '')}</a></li>
        `).join('')}
      </ul>
    `;
  }

  function renderPagination(results, currentPage) {
    const el = document.getElementById('srp-pagination');
    if (!el) return;

    const { nbPages } = results;
    if (nbPages <= 1) { el.hidden = true; return; }

    el.hidden = false;
    const pages = getPaginationRange(currentPage, nbPages);

    el.innerHTML = `
      <button
        is="facet-link"
        class="srp-pagination__prev pagination__link"
        rel="prev"
        aria-label="Previous Page"
        data-page="${currentPage - 1}"
        ${currentPage === 0 ? 'disabled' : ''}>
        &larr; Previous
      </button>

      <span class="srp-pagination__pages">
        ${pages.map(p =>
        p === '…'
          ? `<span class="srp-pagination__ellipsis">…</span>`
          : `<button
                class="srp-pagination__btn srp-pagination__page pagination__link ${p === currentPage ? 'is-active' : ''}"
                data-page="${p}"
                aria-label="Page ${p + 1}"
                ${p === currentPage ? 'disabled' : ''}>
                ${p + 1}
              </button>`
      ).join('')}
      </span>

      <button
        is="facet-link"
        class="srp-pagination__next pagination__link"
        rel="next"
        aria-label="Next"
        data-page="${currentPage + 1}"
        ${currentPage >= nbPages - 1 ? 'disabled' : ''}>
        Next &rarr;
      </button>
    `;

    el.querySelectorAll('button[data-page]:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => {
        const newPage = parseInt(btn.dataset.page, 10);
        const state = { ...getStateFromURL(), page: newPage };
        pushStateToURL(state);
        runSearch(state);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  function renderFacetPlaceholder(results) {
    const el = document.getElementById('srp-facets');
    if (!el) return;

    // PLACEHOLDER: facet panel will render here once attributes are finalized.
    // Uncomment to inspect available facet data in dev:
    // console.log('[SRP] Available facets:', results.facets);
    // console.log('[SRP] Facet stats:', results.facets_stats);

    el.innerHTML = `<p class="srp-facets__placeholder">[Facet filters — pending]</p>`;
  }

  // ==== WINDOW EVENTS ==================================

  window.addEventListener('popstate', () => {
    runSearch(getStateFromURL());
  });

  // ==== INIT =================================================
  // replaceState on init so the initial URL is canonical before the first push

  const initialState = getStateFromURL();
  pushStateToURL(initialState, true);
  runSearch(initialState);

}
